<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import DealKpiCard from './components/DealKpiCard.vue';
import DealStageBadge from './components/DealStageBadge.vue';
import DealFormDialog from './components/DealFormDialog.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { STAGES, STAGE_ORDER, monthLabel, formatMoney } from './constants';

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();
const accountId = useMapGetter('getCurrentAccountId');

const dealRecords = useMapGetter('deals/getDeals');
const dealsMeta = useMapGetter('deals/getMeta');
const dealsUIFlags = useMapGetter('deals/getUIFlags');

const allDeals = computed(() =>
  dealRecords.value.map(record => ({
    id: record.id,
    type: record.deal_type,
    stage: record.stage,
    address: record.address,
    city: record.city,
    client: record.client_name,
    agent: record.agent_name,
    beds: record.beds,
    price: record.price,
    gci: record.gci,
    rent: record.rent,
    keyDate: record.key_date,
    listingUrl: record.listing_url,
    notes: record.notes,
    userId: record.user_id,
    contactId: record.contact_id,
    contactName: record.contact_name,
  }))
);

const contactUrl = contactId =>
  frontendURL(`accounts/${accountId.value}/contacts/${contactId}`);

const fetchDeals = () => store.dispatch('deals/get');
onMounted(() => {
  fetchDeals();
  if (route.query.q) search.value = String(route.query.q);
});

const hasMore = computed(
  () => dealsMeta.value.currentPage < dealsMeta.value.totalPages
);
const loadMore = () => store.dispatch('deals/fetchNextPage');

const dealDialogRef = ref(null);
const openNewDealDialog = () => dealDialogRef.value?.open();
const openEditDealDialog = deal => dealDialogRef.value?.open(deal);

const shareListing = async deal => {
  if (!deal.listingUrl) return;
  try {
    if (navigator.share) {
      await navigator.share({ title: deal.address, url: deal.listingUrl });
    } else {
      await navigator.clipboard.writeText(deal.listingUrl);
      useAlert(t('DEAL_BOARD.LISTING_LINK_COPIED'));
    }
  } catch (error) {
    // Share sheet dismissed by the user, nothing to do.
  }
};

const activeStage = ref('all');
const typeFilter = ref('all');
const search = ref('');
const sortBy = ref('recent');
const bedsFilter = ref('any');
const saleFilter = ref('any');
const rentFilter = ref('any');
const areaFilter = ref('any');

const STAGE_META = computed(() => ({
  pre: {
    label: t('DEAL_BOARD.STAGES.PRE_LISTING'),
    dateLabel: t('DEAL_BOARD.DATE_LABELS.LIST_TARGET'),
  },
  active: {
    label: t('DEAL_BOARD.STAGES.ACTIVE'),
    dateLabel: t('DEAL_BOARD.DATE_LABELS.ON_MARKET'),
  },
  uc: {
    label: t('DEAL_BOARD.STAGES.UNDER_CONTRACT'),
    dateLabel: t('DEAL_BOARD.DATE_LABELS.EST_CLOSE'),
  },
  closed: {
    label: t('DEAL_BOARD.STAGES.CLOSED'),
    dateLabel: t('DEAL_BOARD.DATE_LABELS.CLOSED'),
  },
  vacant: {
    label: t('DEAL_BOARD.STAGES.VACANT'),
    dateLabel: t('DEAL_BOARD.DATE_LABELS.AVAILABLE'),
  },
  leased: {
    label: t('DEAL_BOARD.STAGES.LEASED'),
    dateLabel: t('DEAL_BOARD.DATE_LABELS.LEASE_ENDS'),
  },
}));

const PIPELINE_STAGES = ['pre', 'active', 'uc'];
const sum = (list, key) =>
  list.reduce((total, item) => total + (item[key] || 0), 0);

const pipelineDeals = computed(() =>
  allDeals.value.filter(deal => PIPELINE_STAGES.includes(deal.stage))
);
const closedDeals = computed(() =>
  allDeals.value.filter(deal => deal.stage === 'closed')
);
const leasedDeals = computed(() =>
  allDeals.value.filter(deal => deal.stage === 'leased')
);

const pipelineVolume = computed(() => sum(pipelineDeals.value, 'price'));
const closedVolume = computed(() => sum(closedDeals.value, 'price'));
const closedGci = computed(() => sum(closedDeals.value, 'gci'));
const rentRoll = computed(() => sum(leasedDeals.value, 'rent'));

// Guards against NaN% on brand-new accounts with no closed deals yet.
const closedGciPct = computed(() =>
  closedVolume.value > 0
    ? ((closedGci.value / closedVolume.value) * 100).toFixed(1)
    : '0.0'
);

const kpis = computed(() => [
  {
    label: t('DEAL_BOARD.KPI.PIPELINE_VOLUME'),
    value: formatMoney(pipelineVolume.value),
    subLabel: t('DEAL_BOARD.KPI.PIPELINE_VOLUME_SUB', {
      count: pipelineDeals.value.length,
    }),
  },
  {
    label: t('DEAL_BOARD.KPI.CLOSED_VOLUME'),
    value: formatMoney(closedVolume.value),
    subLabel: t('DEAL_BOARD.KPI.CLOSED_VOLUME_SUB', {
      count: closedDeals.value.length,
    }),
  },
  {
    label: t('DEAL_BOARD.KPI.CLOSED_GCI'),
    value: `$${closedGci.value.toLocaleString('en-US')}`,
    subLabel: t('DEAL_BOARD.KPI.CLOSED_GCI_SUB', {
      pct: closedGciPct.value,
    }),
    valueClass: 'text-n-teal-11',
  },
  {
    label: t('DEAL_BOARD.KPI.RENT_ROLL'),
    value: `$${rentRoll.value.toLocaleString('en-US')}/mo`,
    subLabel: t('DEAL_BOARD.KPI.RENT_ROLL_SUB', {
      count: leasedDeals.value.length,
    }),
  },
]);

const areaOptions = computed(() =>
  [
    ...new Set(
      allDeals.value
        .map(deal => (deal.city || '').split(',')[0])
        .filter(Boolean)
    ),
  ].sort()
);

// Price/rent filter buckets are derived from whatever deals are actually in
// this account, instead of fixed dollar thresholds tuned to one demo
// dataset (which would be meaningless for most other markets).
const buildBuckets = (values, labelPrefix, formatter) => {
  const positives = values.filter(v => typeof v === 'number' && v > 0);
  if (positives.length < 2) return [];
  const min = Math.min(...positives);
  const max = Math.max(...positives);
  if (min === max) return [];

  const low = min + (max - min) / 3;
  const high = min + (2 * (max - min)) / 3;

  return [
    {
      id: 'low',
      label: t(`DEAL_BOARD.${labelPrefix}.UNDER`, { amount: formatter(low) }),
      min: 0,
      max: low,
    },
    {
      id: 'mid',
      label: t(`DEAL_BOARD.${labelPrefix}.RANGE`, {
        min: formatter(low),
        max: formatter(high),
      }),
      min: low,
      max: high,
    },
    {
      id: 'high',
      label: t(`DEAL_BOARD.${labelPrefix}.OVER`, { amount: formatter(high) }),
      min: high,
      max: Infinity,
    },
  ];
};

const saleBuckets = computed(() =>
  buildBuckets(
    allDeals.value.filter(deal => deal.type !== 'Lease').map(deal => deal.price),
    'SALE',
    amount => formatMoney(amount)
  )
);
const rentBuckets = computed(() =>
  buildBuckets(
    allDeals.value.filter(deal => deal.type === 'Lease').map(deal => deal.rent),
    'RENT',
    amount => `$${Math.round(amount).toLocaleString('en-US')}`
  )
);

const matchesPrice = deal => {
  if (deal.type === 'Lease') {
    if (rentFilter.value === 'any') return true;
    const bucket = rentBuckets.value.find(b => b.id === rentFilter.value);
    return !!bucket && deal.rent >= bucket.min && deal.rent < bucket.max;
  }
  if (saleFilter.value === 'any') return true;
  const bucket = saleBuckets.value.find(b => b.id === saleFilter.value);
  return !!bucket && deal.price >= bucket.min && deal.price < bucket.max;
};

const filteredDeals = computed(() => {
  const bedsMin = bedsFilter.value === 'any' ? 0 : Number(bedsFilter.value);
  const needle = search.value.trim().toLowerCase();

  return allDeals.value.filter(deal => {
    if (activeStage.value !== 'all' && deal.stage !== activeStage.value) {
      return false;
    }
    if (typeFilter.value !== 'all' && deal.type !== typeFilter.value) {
      return false;
    }
    if (
      areaFilter.value !== 'any' &&
      (deal.city || '').split(',')[0] !== areaFilter.value
    ) {
      return false;
    }
    if ((deal.beds || 0) < bedsMin) return false;
    if (!matchesPrice(deal)) return false;
    if (needle) {
      const haystack =
        `${deal.address} ${deal.city} ${deal.client} ${deal.agent}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });
});

const priceOf = deal => deal.price ?? (deal.rent ? deal.rent * 12 : 0);
const gciOf = deal => deal.gci ?? (deal.rent ? Math.round(deal.rent * 0.1) : 0);

const SORTERS = {
  recent: (a, b) => (b.keyDate || '').localeCompare(a.keyDate || ''),
  price: (a, b) => priceOf(b) - priceOf(a),
  gci: (a, b) => gciOf(b) - gciOf(a),
  addr: (a, b) => a.address.localeCompare(b.address),
};

const sortedDeals = computed(() =>
  [...filteredDeals.value].sort(SORTERS[sortBy.value])
);

const thumbInitials = address =>
  (address || '')
    .replace(/^\d+\s*/, '')
    .split(' ')
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

const rows = computed(() =>
  sortedDeals.value.map(deal => {
    const stage = STAGES[deal.stage];
    const meta = STAGE_META.value[deal.stage];
    const isLease = deal.type === 'Lease';
    const fee =
      isLease && deal.rent ? Math.round(deal.rent * 0.1) : null;

    return {
      key: deal.id ?? deal.address,
      raw: deal,
      thumb: thumbInitials(deal.address),
      address: deal.address,
      cityLine: `${deal.city || '—'} · ${deal.beds ?? '—'} bd`,
      type: deal.type,
      client: deal.client,
      agent: deal.agent,
      stageLabel: meta?.label,
      badgeClass: stage?.badge,
      dotClass: stage?.dot,
      price: isLease
        ? `${deal.rent ? `$${deal.rent.toLocaleString('en-US')}/mo` : '—'}`
        : formatMoney(deal.price),
      gci: isLease
        ? `${fee !== null ? `$${fee.toLocaleString('en-US')}/mo` : '—'}`
        : `${deal.gci !== null && deal.gci !== undefined ? `$${deal.gci.toLocaleString('en-US')}` : '—'}`,
      gciSub: isLease
        ? t('DEAL_BOARD.TABLE.MGMT_FEE')
        : deal.gci && deal.price
          ? t('DEAL_BOARD.TABLE.SIDE_PCT', {
              pct: ((deal.gci / deal.price) * 100).toFixed(1),
            })
          : '',
      gciClass: deal.stage === 'closed' ? 'text-n-teal-11' : 'text-n-slate-12',
      dateA: monthLabel(deal.keyDate, locale.value),
      dateB: meta?.dateLabel,
    };
  })
);

const stageTabs = computed(() => [
  { id: 'all', label: t('DEAL_BOARD.TABS.ALL'), count: allDeals.value.length },
  ...STAGE_ORDER.map(stageId => ({
    id: stageId,
    label: STAGE_META.value[stageId].label,
    count: allDeals.value.filter(deal => deal.stage === stageId).length,
  })),
]);

const hasActiveFilters = computed(
  () =>
    bedsFilter.value !== 'any' ||
    saleFilter.value !== 'any' ||
    rentFilter.value !== 'any' ||
    areaFilter.value !== 'any'
);

const clearFilters = () => {
  bedsFilter.value = 'any';
  saleFilter.value = 'any';
  rentFilter.value = 'any';
  areaFilter.value = 'any';
};

const footerText = computed(() =>
  t('DEAL_BOARD.FOOTER_COUNT', {
    shown: rows.value.length,
    total: allDeals.value.length,
  })
);

const selectClass =
  'rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand';
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-y-auto bg-n-background">
    <div class="mx-auto w-full max-w-[1280px] px-8 py-7">
      <!-- Header -->
      <div class="flex items-center gap-3.5">
        <div
          class="flex size-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-n-brand text-[17px] font-bold text-white"
        >
          D
        </div>
        <div class="flex flex-col gap-0.5">
          <div class="text-xl font-bold tracking-tight text-n-slate-12">
            {{ t('DEAL_BOARD.TITLE') }}
          </div>
          <div class="text-[13px] text-n-slate-11">
            {{ t('DEAL_BOARD.SUBTITLE') }}
          </div>
        </div>
        <div class="flex-1" />
        <Button
          :label="t('DEAL_BOARD.NEW_DEAL')"
          color="blue"
          size="sm"
          @click="openNewDealDialog"
        />
      </div>

      <!-- Loading state -->
      <div
        v-if="dealsUIFlags.isFetching"
        class="mt-10 flex flex-col items-center gap-2 py-16 text-center"
      >
        <div class="text-sm font-semibold text-n-slate-11">
          {{ t('DEAL_BOARD.LOADING') }}
        </div>
      </div>

      <!-- Error state: only takes over the whole view when we have nothing
           to show at all. A failed refresh after deals are already loaded
           (or just created) shouldn't hide the board the user is looking
           at -- that would also hide anything they create while it's up. -->
      <div
        v-else-if="dealsUIFlags.hasFetchError && !allDeals.length"
        class="mt-10 flex flex-col items-center gap-3 py-16 text-center"
      >
        <div class="text-sm font-semibold text-n-slate-11">
          {{ t('DEAL_BOARD.ERROR.TITLE') }}
        </div>
        <div class="text-[13px] text-n-slate-10">
          {{ t('DEAL_BOARD.ERROR.SUBTITLE') }}
        </div>
        <Button
          :label="t('DEAL_BOARD.ERROR.RETRY')"
          size="sm"
          variant="outline"
          @click="fetchDeals"
        />
      </div>

      <template v-else>
        <!-- KPIs -->
        <div class="mt-6 grid grid-cols-4 gap-3.5">
          <DealKpiCard
            v-for="kpi in kpis"
            :key="kpi.label"
            :label="kpi.label"
            :value="kpi.value"
            :sub-label="kpi.subLabel"
            :value-class="kpi.valueClass"
          />
        </div>

        <!-- Toolbar -->
        <div class="mt-7 flex flex-col gap-3">
          <div class="flex gap-1 overflow-x-auto border-b border-n-weak">
            <button
              v-for="tab in stageTabs"
              :key="tab.id"
              type="button"
              class="-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 pb-3 pt-2.5 text-[13.5px] font-semibold"
              :class="
                activeStage === tab.id
                  ? 'border-n-brand text-n-slate-12'
                  : 'border-transparent text-n-slate-11'
              "
              @click="activeStage = tab.id"
            >
              {{ tab.label }}
              <span
                class="rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-[11.5px] font-semibold text-n-slate-11"
              >
                {{ tab.count }}
              </span>
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-2.5">
            <div class="w-32">
              <select v-model="typeFilter" :class="selectClass">
                <option value="all">{{ t('DEAL_BOARD.TYPE.ALL') }}</option>
                <option value="Purchase">
                  {{ t('DEAL_BOARD.TYPE.PURCHASE') }}
                </option>
                <option value="Listing">
                  {{ t('DEAL_BOARD.TYPE.LISTING') }}
                </option>
                <option value="Lease">{{ t('DEAL_BOARD.TYPE.LEASE') }}</option>
              </select>
            </div>
            <div class="w-28">
              <select v-model="bedsFilter" :class="selectClass">
                <option value="any">{{ t('DEAL_BOARD.BEDS.ANY') }}</option>
                <option value="2">{{ t('DEAL_BOARD.BEDS.TWO') }}</option>
                <option value="3">{{ t('DEAL_BOARD.BEDS.THREE') }}</option>
                <option value="4">{{ t('DEAL_BOARD.BEDS.FOUR') }}</option>
              </select>
            </div>
            <div v-if="saleBuckets.length" class="w-36">
              <select v-model="saleFilter" :class="selectClass">
                <option value="any">{{ t('DEAL_BOARD.SALE.ANY') }}</option>
                <option
                  v-for="bucket in saleBuckets"
                  :key="bucket.id"
                  :value="bucket.id"
                >
                  {{ bucket.label }}
                </option>
              </select>
            </div>
            <div v-if="rentBuckets.length" class="w-40">
              <select v-model="rentFilter" :class="selectClass">
                <option value="any">{{ t('DEAL_BOARD.RENT.ANY') }}</option>
                <option
                  v-for="bucket in rentBuckets"
                  :key="bucket.id"
                  :value="bucket.id"
                >
                  {{ bucket.label }}
                </option>
              </select>
            </div>
            <div class="w-32">
              <select v-model="areaFilter" :class="selectClass">
                <option value="any">{{ t('DEAL_BOARD.AREA.ALL') }}</option>
                <option v-for="area in areaOptions" :key="area" :value="area">
                  {{ area }}
                </option>
              </select>
            </div>
            <div class="w-52">
              <input
                v-model="search"
                type="text"
                :placeholder="t('DEAL_BOARD.SEARCH_PLACEHOLDER')"
                class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
              />
            </div>
            <div class="w-36">
              <select v-model="sortBy" :class="selectClass">
                <option value="recent">{{ t('DEAL_BOARD.SORT.RECENT') }}</option>
                <option value="price">{{ t('DEAL_BOARD.SORT.PRICE') }}</option>
                <option value="gci">{{ t('DEAL_BOARD.SORT.GCI') }}</option>
                <option value="addr">{{ t('DEAL_BOARD.SORT.ADDRESS') }}</option>
              </select>
            </div>
            <button
              v-if="hasActiveFilters"
              type="button"
              class="px-1 text-[12.5px] font-semibold text-n-blue-text"
              @click="clearFilters"
            >
              {{ t('DEAL_BOARD.CLEAR_FILTERS') }}
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="mt-4 overflow-x-auto">
          <div class="min-w-[1060px]">
            <div
              class="grid grid-cols-[minmax(225px,1.6fr)_88px_minmax(155px,1.2fr)_142px_minmax(95px,0.9fr)_minmax(95px,0.9fr)_minmax(125px,1fr)] gap-3 px-3.5 pb-2.5 pt-3 text-[11px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              <div>{{ t('DEAL_BOARD.COLUMNS.PROPERTY') }}</div>
              <div>{{ t('DEAL_BOARD.COLUMNS.TYPE') }}</div>
              <div>{{ t('DEAL_BOARD.COLUMNS.CLIENT_AGENT') }}</div>
              <div>{{ t('DEAL_BOARD.COLUMNS.STAGE') }}</div>
              <div>{{ t('DEAL_BOARD.COLUMNS.PRICE') }}</div>
              <div>{{ t('DEAL_BOARD.COLUMNS.GCI_FEE') }}</div>
              <div>{{ t('DEAL_BOARD.COLUMNS.KEY_DATE') }}</div>
            </div>

            <div
              class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
            >
              <div
                v-for="row in rows"
                :key="row.key"
                class="grid cursor-pointer grid-cols-[minmax(225px,1.6fr)_88px_minmax(155px,1.2fr)_142px_minmax(95px,0.9fr)_minmax(95px,0.9fr)_minmax(125px,1fr)] items-center gap-3 border-b border-n-weak px-3.5 py-3.5 last:border-b-0 hover:bg-n-alpha-2"
                @click="openEditDealDialog(row.raw)"
              >
                <div class="flex min-w-0 items-center gap-3">
                  <div
                    class="flex size-[42px] flex-shrink-0 items-center justify-center rounded-lg border border-n-weak bg-n-slate-3 font-mono text-[11px] font-semibold text-n-slate-10"
                  >
                    {{ row.thumb }}
                  </div>
                  <div class="min-w-0">
                    <div class="flex items-center gap-1.5">
                      <span
                        class="truncate text-[13.5px] font-semibold text-n-slate-12"
                      >
                        {{ row.address }}
                      </span>
                      <a
                        :href="`/api/v1/template_builder_auto_login?deal_id=${row.raw.id}`"
                        target="_blank"
                        rel="noopener noreferrer"
                        :title="t('DEAL_BOARD.CREATE_WEBSITE')"
                        class="i-lucide-layout-template size-3.5 flex-shrink-0 text-n-slate-10 hover:text-n-blue-text"
                        @click.stop
                      />
                      <template v-if="row.raw.listingUrl">
                        <a
                          :href="row.raw.listingUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          :title="t('DEAL_BOARD.OPEN_LISTING')"
                          class="i-lucide-external-link size-3.5 flex-shrink-0 text-n-slate-10 hover:text-n-blue-text"
                          @click.stop
                        />
                        <button
                          type="button"
                          :title="t('DEAL_BOARD.SHARE_LISTING')"
                          class="i-lucide-share-2 size-3.5 flex-shrink-0 text-n-slate-10 hover:text-n-blue-text"
                          @click.stop="shareListing(row.raw)"
                        />
                      </template>
                    </div>
                    <div class="mt-0.5 text-xs text-n-slate-10">
                      {{ row.cityLine }}
                    </div>
                  </div>
                </div>
                <div class="text-sm text-n-slate-11">{{ row.type }}</div>
                <div class="min-w-0">
                  <a
                    v-if="row.raw.contactId"
                    :href="contactUrl(row.raw.contactId)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :title="t('DEAL_BOARD.VIEW_CONTACT')"
                    class="truncate text-sm font-semibold text-n-blue-text hover:underline"
                    @click.stop
                  >
                    {{ row.client }}
                  </a>
                  <div
                    v-else
                    class="truncate text-sm font-semibold text-n-slate-12"
                  >
                    {{ row.client }}
                  </div>
                  <div class="mt-0.5 truncate text-xs text-n-slate-10">
                    {{ row.agent }}
                  </div>
                </div>
                <div>
                  <DealStageBadge
                    :label="row.stageLabel"
                    :badge-class="row.badgeClass"
                    :dot-class="row.dotClass"
                  />
                </div>
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ row.price }}
                </div>
                <div>
                  <div class="text-sm font-bold" :class="row.gciClass">
                    {{ row.gci }}
                  </div>
                  <div class="mt-0.5 text-xs text-n-slate-10">
                    {{ row.gciSub }}
                  </div>
                </div>
                <div>
                  <div class="text-sm font-semibold text-n-slate-12">
                    {{ row.dateA }}
                  </div>
                  <div class="mt-0.5 text-xs text-n-slate-10">
                    {{ row.dateB }}
                  </div>
                </div>
              </div>

              <div v-if="!rows.length" class="px-5 py-12 text-center">
                <div class="text-sm font-semibold text-n-slate-11">
                  {{
                    allDeals.length
                      ? t('DEAL_BOARD.EMPTY.TITLE')
                      : t('DEAL_BOARD.EMPTY.NO_DEALS_TITLE')
                  }}
                </div>
                <div class="mt-1 text-[13px] text-n-slate-10">
                  {{
                    allDeals.length
                      ? t('DEAL_BOARD.EMPTY.SUBTITLE')
                      : t('DEAL_BOARD.EMPTY.NO_DEALS_SUBTITLE')
                  }}
                </div>
              </div>
            </div>

            <div class="flex items-center justify-between px-1 py-3">
              <div class="text-[12.5px] text-n-slate-10">
                {{ footerText }}
              </div>
              <button
                v-if="hasMore"
                type="button"
                class="text-[12.5px] font-semibold text-n-blue-text disabled:opacity-50"
                :disabled="dealsUIFlags.isFetchingMore"
                @click="loadMore"
              >
                {{
                  dealsUIFlags.isFetchingMore
                    ? t('DEAL_BOARD.LOADING_MORE')
                    : t('DEAL_BOARD.LOAD_MORE')
                }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>

    <DealFormDialog ref="dealDialogRef" />
  </div>
</template>
